import React, { useState, useEffect } from 'react'
import { Button, FormGroup } from 'react-bootstrap'
import ReactSelect, { Option } from 'react-select'

import Label from 'live/components/forms/ui/label'
import authorization from 'live/services/authorization'
import i18n from 'live/services/i18n'

import { IBarriersListFilters, listBarriers, listBarrierTypes, getBarrier } from '../../../../../../../services'
import { IBarrier, IBarrierType, IMethod, ISelectOptionType } from '../../../../../../../types'
import { IEditorBarrier, IEditorEscalation, IUpdateMethodsOptions } from '../../../../BarrierEditor'
import { BarrierInfoContainer, InfoBarrierFlex } from './styles'

type IProps = {
    handleChange: () => void
    handleGetRecursiveSearch: (k: IEditorBarrier) => IEditorBarrier[]
    item: IEditorBarrier[]
    setIsEditModal: React.Dispatch<React.SetStateAction<boolean>>
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
    updateModeMethods: IUpdateMethodsOptions[]
    // selectedMethod: IMethod
}

export const AssignModal: React.FC<IProps> = ({
    item,
    handleGetRecursiveSearch,
    handleChange,
    setLoading,
    setIsEditModal
    // updateModeMethods
}) => {
    const [barrierTypes, setBarrierTypes] = useState<IBarrierType[]>([])

    const [method, setMethod] = useState<IMethod>()
    const [barriers, setBarriers] = useState<Map<number, IBarrier>>(new Map())
    const [selectedBarrierType, setSelectedBarrierType] = useState<ISelectOptionType>()
    const [selectedBarrier, setSelectedBarrier] = useState<IBarrier>()

    const handleSelectBarrier = async (id: number): Promise<void> => {
        try {
            setLoading(true)
            const barrier = (await getBarrier(id)) as IEditorBarrier
            barrier.getFather = item[0].getFather
            const existingReferences = handleGetRecursiveSearch(barrier)

            if (existingReferences.length > 0) {
                setSelectedBarrier(existingReferences[0])
                item.forEach(el => {
                    el.updateModeCode = existingReferences[0].updateModeCode
                    el.updateModeJsonConfig = existingReferences[0].updateModeJsonConfig
                    el.barrierType = existingReferences[0].barrierType
                    el.criticality = existingReferences[0].criticality
                    el.escalations = existingReferences[0].escalations
                    el.id = existingReferences[0].id
                    el.name = existingReferences[0].name
                    el.supervisor = existingReferences[0].supervisor
                    el.getFather = existingReferences[0].getFather
                })
            } else {
                setSelectedBarrier(barrier)
                item.forEach(el => {
                    el.updateModeCode = barrier.updateModeCode
                    el.updateModeJsonConfig = barrier.updateModeJsonConfig
                    el.barrierType = barrier.barrierType
                    el.criticality = barrier.criticality
                    el.escalations = barrier.escalations
                    el.id = barrier.id
                    el.name = barrier.name
                    el.supervisor = barrier.supervisor
                    el.getFather = barrier.getFather
                })
            }

            handleChange()
        } catch (e) {
        } finally {
            setLoading(false)
        }
    }

    const handleBarrierTypeChange = (object: ISelectOptionType): void => {
        setSelectedBarrierType(object)
        setSelectedBarrier(undefined)
    }

    const handleValidBarrier = (
        reference: IEditorBarrier[] | IEditorEscalation[] | IEditorBarrier | IEditorEscalation,
        fetchedBarriers: Map<number, IBarrier>
    ): void => {
        if (Array.isArray(reference)) {
            if ((reference[0] as IEditorBarrier).barrierType) {
                ;(reference as IEditorBarrier[]).forEach(bar => {
                    if (fetchedBarriers.has(bar.id)) {
                        fetchedBarriers.delete(bar.id)
                    }
                    if (bar.getFather) {
                        handleValidBarrier(bar.getFather, fetchedBarriers)
                    }
                })
            } else {
                ;(reference as IEditorEscalation[]).forEach(el => {
                    if (el.getFather) {
                        handleValidBarrier(el.getFather, fetchedBarriers)
                    }
                })
            }
        } else {
            if ((reference as IBarrier).barrierType) {
                if (fetchedBarriers.has(reference.id)) {
                    fetchedBarriers.delete(reference.id)
                }

                if (reference.getFather) {
                    handleValidBarrier(reference.getFather, fetchedBarriers)
                }
            } else {
                if (reference.getFather) {
                    handleValidBarrier(reference.getFather, fetchedBarriers)
                }
            }
        }
    }

    useEffect(() => {
        const handleOpen = async (): Promise<void> => {
            setLoading(true)

            const barrierTypesFetch = await listBarrierTypes()

            const method = item?.at(0)?.method

            setMethod(method)

            const barrierListParams: IBarriersListFilters = {
                filterBy: item[0]?.asset.id,
                root: 'false'
            }
            const barriersFetch = await listBarriers(barrierListParams)
            setBarrierTypes(barrierTypesFetch.data)
            const fetchedBarriers = barriersFetch.data
                .filter(e => !e.root)
                .reduce((prev, curr) => prev.set(curr.id, curr), new Map<number, IBarrier>())

            await handleValidBarrier(item as IEditorBarrier[], fetchedBarriers)

            setBarriers(fetchedBarriers)

            if (item && item[0].barrierType) {
                setSelectedBarrier(item[0])
                setSelectedBarrierType({
                    label: item[0].barrierType?.name,
                    value: item[0].barrierType?.id
                })
            } else {
                setSelectedBarrierType(undefined)
                setSelectedBarrier(undefined)
            }

            setLoading(false)
        }

        handleOpen()
    }, [item]) // eslint-disable-line react-hooks/exhaustive-deps

    const bowtieAdmin = authorization.superAdmin || authorization.Permission.BOWTIE_ADMIN
    const barrierFilter = (barrier: IBarrier): boolean =>
        selectedBarrierType?.value === barrier.barrierType?.id && method?.id === barrier?.method?.id

    return (
        <>
            <FormGroup>
                <Label required className="control-label">
                    {i18n('Barrier Category')}
                </Label>
                {bowtieAdmin ? (
                    <ReactSelect
                        options={barrierTypes.map(bt => ({ value: bt.id, label: bt.name }))}
                        id="barrierEditor-barrierAssignModal-field-barrierType"
                        onChange={handleBarrierTypeChange}
                        clearable={false}
                        value={selectedBarrierType}
                        searchable={true}
                    />
                ) : (
                    <ReactSelect
                        disabled
                        options={barrierTypes.map(bt => ({ value: bt.id, label: bt.name }))}
                        id="barrierEditor-barrierAssignModal-field-barrierType"
                        onChange={handleBarrierTypeChange}
                        clearable={false}
                        value={selectedBarrierType}
                        searchable={true}
                    />
                )}
            </FormGroup>
            <FormGroup>
                <Label required className="control-label">
                    {i18n('Barrier')}
                </Label>
                {bowtieAdmin ? (
                    <ReactSelect
                        disabled={!selectedBarrierType}
                        id="barrierEditor-barrierAssignModal-field-barrier"
                        options={Array.from(barriers)
                            .filter(([, bar]) => barrierFilter(bar))
                            .filter(([, bar]) => barrierFilter(bar))
                            .map(([, bar]) => ({ value: bar.id, label: bar.name }))}
                        clearable={false}
                        onChange={(opt: Option<number>) => handleSelectBarrier(opt.value)}
                        value={{ label: selectedBarrier?.name, value: selectedBarrier?.id }}
                        searchable={true}
                    />
                ) : (
                    <ReactSelect
                        disabled
                        id="barrierEditor-barrierAssignModal-field-barrier"
                        options={Array.from(barriers)
                            .filter(([, bar]) => barrierFilter(bar))
                            .filter(([, bar]) => barrierFilter(bar))
                            .map(([, bar]) => ({ value: bar.id, label: bar.name }))}
                        clearable={false}
                        onChange={(opt: Option<number>) => handleSelectBarrier(opt.value)}
                        value={{ label: selectedBarrier?.name, value: selectedBarrier?.id }}
                        searchable={true}
                    />
                )}
            </FormGroup>
            <hr />
            {selectedBarrier ? (
                <BarrierInfoContainer>
                    <div className="barrier-info-row barrier">
                        {i18n('BARRIER')} <div className="barrier-info-id">#{selectedBarrier.id ?? '--'}</div>
                        {bowtieAdmin && <i className="fas fa-edit" onClick={() => setIsEditModal(true)} />}
                    </div>
                    <div className="barrier-info-row description">{selectedBarrier.name ?? ''}</div>
                    <hr />
                    <div className="barrier-info-row info">
                        <div className="key">{i18n('Responsible')}</div>
                        <div className="value">{selectedBarrier.supervisor?.name ?? ''}</div>
                    </div>
                    <div className="barrier-info-row info">
                        <div className="key">{i18n('Barrier Category')}</div>
                        <div className="value">{selectedBarrier.barrierType?.name ?? ''}</div>
                    </div>
                    <div className="barrier-info-row info">
                        <div className="key">{i18n('Criticity')}</div>
                        <div className="value">{selectedBarrier.criticality?.level ?? ''}</div>
                    </div>
                    <hr />
                </BarrierInfoContainer>
            ) : (
                bowtieAdmin && (
                    <BarrierInfoContainer>
                        <InfoBarrierFlex>
                            <div className="select-barrier-label">
                                {i18n('Please select barrier category and barrier')}
                            </div>
                            <Button bsStyle="primary" onClick={() => setIsEditModal(true)}>
                                {i18n('New Barrier')}
                            </Button>
                        </InfoBarrierFlex>
                    </BarrierInfoContainer>
                )
            )}
        </>
    )
}
